import { useMemo, useState } from "react";
import {
	Box,
	Button,
	Divider,
	IconButton,
	Popover,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { useAtomValue } from "jotai";
import { runItemAnalysisPDF } from "./ItemAnalysisPDF";
import { examProcessingStateAtom, outputSettingsAtom } from "../../../components/Atoms/atoms";
import isAuthenticated from "../../../components/Utils/isAuthenticated";

function ScoreStrip({ dist = [] }) {
	const max = useMemo(() => Math.max(1, ...dist.map((d) => d.freq || 0)), [dist]);

	return (
		<Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.5, height: 32 }}>
			{dist.map((d, i) => {
				const height = (d.freq / max) * 28;
				return (
					<Box
						key={i}
						title={`Score ${d.score}: ${d.freq}`}
						sx={{
							width: 6,
							height: height || 2,
							borderRadius: "2px",
							backgroundColor: "action.active",
						}}
					/>
				);
			})}
		</Box>
	);
}

function Row({ label, value }) {
	return (
		<TableRow>
			<TableCell sx={{ pr: 2, color: "text.secondary", border: 0, py: 0.5 }}>{label}</TableCell>
			<TableCell align="right" sx={{ border: 0, py: 0.5, fontWeight: 600 }}>
				{value}
			</TableCell>
		</TableRow>
	);
}

function StatCard({ label, currVal, origVal, decimals = 2 }) {
	const currentText = fmt(currVal, decimals);
	const showOriginal = origVal != null && !equalRounded(currVal, origVal, decimals);

	return (
		<Box
			sx={{
				border: "1px solid",
				borderColor: "divider",
				borderRadius: 1.5,
				p: 1.25,
				backgroundColor: "background.paper",
			}}
		>
			<Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
				{label}
			</Typography>
			<Typography variant="body2" sx={{ fontWeight: 700 }}>
				{currentText}
			</Typography>
			{showOriginal && (
				<Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.25 }}>
					Original {fmt(origVal, decimals)}
				</Typography>
			)}
		</Box>
	);
}

const num = (v) => (v == null || v === "" ? null : Number(v));
const fmt = (v, d = 2) => (v == null || Number.isNaN(Number(v)) ? "-" : Number(v).toFixed(d));
const equalRounded = (c, o, d = 2) => {
	const current = num(c);
	const original = num(o);
	if (current == null || original == null || Number.isNaN(current) || Number.isNaN(original)) {
		return false;
	}
	return current.toFixed(d) === original.toFixed(d);
};

function Stat({ label, currVal, origVal, decimals = 2 }) {
	const currentText = fmt(currVal, decimals);
	const showOriginal = origVal != null && !equalRounded(currVal, origVal, decimals);
	const suffix = showOriginal ? ` (Original: ${fmt(origVal, decimals)})` : "";

	return <Row label={label} value={`${currentText}${suffix}`} />;
}

function KeyStatsSummary({
	analysis,
	versionLabel,
	rangesForDisplay,
	showDownload,
	onDownload,
	displayMode,
}) {
	const currentStats = analysis?.analysis || {};
	const originalStats = currentStats?.original_key_stats || {};

	const currentKr20 = num(currentStats.kr20);
	const currentSd = num(currentStats.sd_total);
	const currentSem =
		currentSd != null && currentKr20 != null ? currentSd * Math.sqrt(1 - currentKr20) : null;

	const originalKr20 = num(originalStats.kr20);
	const originalSd = num(originalStats.sd_total);
	const originalSem =
		originalSd != null && originalKr20 != null ? originalSd * Math.sqrt(1 - originalKr20) : null;

	const dist = Array.isArray(currentStats?.score_dist_table) ? currentStats.score_dist_table : [];

	if (displayMode === "inline") {
		return (
			<Box
				sx={{
					mb: 2,
					border: "1px solid",
					borderColor: "divider",
					borderRadius: 2,
					p: 1.5,
					backgroundColor: "white",
				}}
			>
				<Stack
					direction={{ xs: "column", sm: "row" }}
					alignItems={{ xs: "flex-start", sm: "center" }}
					justifyContent="space-between"
					spacing={1}
					sx={{ mb: analysis ? 1.5 : 0 }}
				>
					<Box>
						<Typography variant="overline" sx={{ color: "text.secondary", lineHeight: 1.2 }}>
							Item Analysis
						</Typography>
						<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
							Key {versionLabel}
						</Typography>
						{rangesForDisplay && (
							<Typography
								variant="caption"
								sx={{ mt: 0.25, display: "block", color: "text.secondary" }}
							>
								Included items: {rangesForDisplay}
							</Typography>
						)}
					</Box>

					{showDownload && analysis && (
						<Button
							size="small"
							variant="text"
							onClick={onDownload}
							startIcon={<PictureAsPdfOutlinedIcon />}
						>
							Download Single Analysis
						</Button>
					)}
				</Stack>

				{analysis ? (
					<>
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: {
									xs: "repeat(2, minmax(0, 1fr))",
									lg: "repeat(3, minmax(0, 1fr))",
								},
								gap: 1,
							}}
						>
							<StatCard
								label="Examinees"
								currVal={currentStats.student_count}
								origVal={originalStats.student_count}
								decimals={0}
							/>
							<StatCard
								label="Mean (Total)"
								currVal={currentStats.mean_total}
								origVal={originalStats.mean_total}
							/>
							<StatCard
								label="Std Dev (Total)"
								currVal={currentStats.sd_total}
								origVal={originalStats.sd_total}
							/>
							<StatCard
								label="Variance (Total)"
								currVal={currentStats.var_total}
								origVal={originalStats.var_total}
							/>
							<StatCard
								label="KR-20"
								currVal={currentStats.kr20}
								origVal={originalStats.kr20}
								decimals={4}
							/>
							<StatCard
								label="S.E. of Measurement"
								currVal={currentSem}
								origVal={originalSem}
								decimals={4}
							/>
						</Box>

						{dist.length > 0 && (
							<Box sx={{ mt: 1.5 }}>
								<Typography
									variant="caption"
									sx={{ mb: 0.5, display: "block", color: "text.secondary" }}
								>
									Score distribution
								</Typography>
								<ScoreStrip dist={dist} />
							</Box>
						)}
					</>
				) : (
					<Typography variant="body2" sx={{ color: "text.secondary" }}>
						Item analysis is not available yet for this key.
					</Typography>
				)}
			</Box>
		);
	}

	return (
		<>
			<Stack direction="row" alignItems="center" justifyContent="space-between">
				<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
					Key {versionLabel}
				</Typography>
				{showDownload && (
					<Button
						size="small"
						variant="text"
						onClick={onDownload}
						startIcon={<PictureAsPdfOutlinedIcon />}
					>
						Download Single Analysis
					</Button>
				)}
			</Stack>

			{rangesForDisplay && (
				<Typography variant="caption" sx={{ mt: 0.5, display: "block", opacity: 0.8 }}>
					Included items: {rangesForDisplay}
				</Typography>
			)}

			<Divider sx={{ my: 1 }} />

			<Table size="small" sx={{ "& td, & th": { border: 0 } }}>
				<TableBody>
					<Stat
						label="Examinees"
						currVal={currentStats.student_count}
						origVal={originalStats.student_count}
						decimals={0}
					/>
					<Stat
						label="Mean (Total)"
						currVal={currentStats.mean_total}
						origVal={originalStats.mean_total}
					/>
					<Stat
						label="Std Dev (Total)"
						currVal={currentStats.sd_total}
						origVal={originalStats.sd_total}
					/>
					<Stat
						label="Variance (Total)"
						currVal={currentStats.var_total}
						origVal={originalStats.var_total}
					/>
					<Stat
						label="KR-20"
						currVal={currentStats.kr20}
						origVal={originalStats.kr20}
						decimals={4}
					/>
					<Row
						label="S.E. of Measurement"
						value={`${fmt(currentSem, 4)}${
							originalSem != null && !equalRounded(currentSem, originalSem, 4)
								? ` (was ${fmt(originalSem, 4)})`
								: ""
						}`}
					/>
				</TableBody>
			</Table>

			{dist.length > 0 && (
				<>
					<Divider sx={{ my: 1 }} />
					<Typography variant="caption" sx={{ mb: 0.5, display: "block" }}>
						Score distribution
					</Typography>
					<ScoreStrip dist={dist} />
				</>
			)}
		</>
	);
}

export function KeyStatsDisplay({
	versionLabel,
	analyses,
	onOpenFullPDF,
	rangesForDisplay,
	displayMode = "popover",
}) {
	const [anchorEl, setAnchorEl] = useState(null);
	const auth = isAuthenticated();
	const token = auth?.token || null;
	const examProcessingState = useAtomValue(examProcessingStateAtom);
	const outputSettings = useAtomValue(outputSettingsAtom);

	const handleDownloadItemAnalysisPDF = async () => {
		const nextOutputSettings = {
			...outputSettings,
			printAllVersions: false,
			versionToPrint: versionLabel,
		};

		await runItemAnalysisPDF({
			examProcessingState,
			token,
			outputSettings: nextOutputSettings,
		});
	};

	const analysis = useMemo(() => {
		if (!Array.isArray(analyses) || analyses.length === 0) return null;
		const hit = analyses.find(
			(item) => String(item.version).trim() === String(versionLabel).trim(),
		);
		return hit || (analyses.length === 1 ? analyses[0] : null);
	}, [analyses, versionLabel]);

	if (displayMode === "inline") {
		return (
			<KeyStatsSummary
				analysis={analysis}
				versionLabel={versionLabel}
				rangesForDisplay={rangesForDisplay}
				showDownload={Boolean(onOpenFullPDF)}
				onDownload={handleDownloadItemAnalysisPDF}
				displayMode="inline"
			/>
		);
	}

	return (
		<>
			<Tooltip title="Item analysis stats">
				<IconButton
					size="small"
					onClick={(event) => setAnchorEl(event.currentTarget)}
					sx={{ ml: 0.5 }}
					disabled={!analysis}
				>
					<AssessmentIcon color="secondaryPurple" sx={{ width: 32, height: 32 }} />
				</IconButton>
			</Tooltip>

			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
				PaperProps={{ sx: { p: 1.5, minWidth: 420 } }}
			>
				<KeyStatsSummary
					analysis={analysis}
					versionLabel={versionLabel}
					rangesForDisplay={rangesForDisplay}
					showDownload={Boolean(onOpenFullPDF)}
					onDownload={handleDownloadItemAnalysisPDF}
					displayMode="popover"
				/>
			</Popover>
		</>
	);
}
